import { navigationActions } from "@tm/insurance/navigation";
import { FormattedText } from "@tm/static";
import { LandingButton } from "@tm/ui-widgets/components";
import { useCallback, useMemo } from "react";
import { useCancelPolicyStatic } from "../CancelPolicyContext";
import type {
  CancelPolicyManageInsuranceAction,
  CancelPolicyManageInsuranceData,
} from "../types";
import { buildOrderDetailsNavigationParams } from "../utils/buildOrderDetailsNavigationParams";
import {
  ManageInsuranceActions,
  ManageInsuranceCard,
  ManageInsuranceHeader,
  ManageInsuranceIcon,
  manageSubTitleStyle,
  manageTitleStyle,
} from "./CancelPolicyManageInsurance.style";

export const CancelPolicyManageInsurance = ({
  header,
  actionsList,
}: CancelPolicyManageInsuranceData) => {
  const { bookingId, query } = useCancelPolicyStatic();

  const orderDetailsNavParams = useMemo(
    () => buildOrderDetailsNavigationParams(bookingId, query),
    [bookingId, query]
  );

  const handleAction = useCallback(
    (action: CancelPolicyManageInsuranceAction) => {
      if (action.actionId === "NAVIGATE_TO_ORDERS") {
        navigationActions.navigateToOrderDetailsV2(orderDetailsNavParams);
      }
    },
    [orderDetailsNavParams]
  );

  const onActionClick = useCallback(
    (action: CancelPolicyManageInsuranceAction) => () => handleAction(action),
    [handleAction]
  );

  if (!header) {
    return null;
  }

  return (
    <ManageInsuranceCard>
      {header?.icon && (
        <ManageInsuranceIcon src={header?.icon} alt="" aria-hidden="true" />
      )}

      {header?.title || header?.subTitle ? (
        <ManageInsuranceHeader>
          {header?.title ? (
            <FormattedText value={header.title} baseStyle={manageTitleStyle} />
          ) : null}
          {header?.subTitle ? (
            <FormattedText
              value={header.subTitle}
              baseStyle={manageSubTitleStyle}
            />
          ) : null}
        </ManageInsuranceHeader>
      ) : null}

      {actionsList?.length ? (
        <ManageInsuranceActions>
          {actionsList.map((action, index) => {
            if (action.isVisible === false) return null;
            const label = typeof action.label === "string" ? action.label : "";
            if (!label) return null;
            return (
              <LandingButton
                key={action.id ?? action.actionId ?? `manage-action-${index}`}
                customTitle={label}
                customWidth="100%"
                disableState={action.isEnabled === false}
                onClick={onActionClick(action)}
              />
            );
          })}
        </ManageInsuranceActions>
      ) : null}
    </ManageInsuranceCard>
  );
};

export default CancelPolicyManageInsurance;
